import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2,
  BrainCircuit,
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  Eye,
  ShieldAlert,
  Wrench,
  Database,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface LlmEntry {
  id: string;
  conversation_id: string;
  message_id: string | null;
  profile_id: string | null;
  tool_input: any;
  tool_output: string | null;
  connector: string | null;
  created_at: string;
}

interface ToolCallAction {
  id: string;
  tool_name: string;
  tool_input: any;
  tool_output: string | null;
  connector: string | null;
  created_at: string;
}

interface UserOption {
  id: string;
  label: string;
}

const PAGE_SIZE = 50;

const LlmObservability = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState<LlmEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedMsgIdx, setExpandedMsgIdx] = useState<number | null>(null);
  const [expandedToolIdx, setExpandedToolIdx] = useState<number | null>(null);

  // MCP tool calls for expanded entry
  const [toolCalls, setToolCalls] = useState<ToolCallAction[]>([]);
  const [toolCallsLoading, setToolCallsLoading] = useState(false);

  // Filters
  const [filterUser, setFilterUser] = useState<string>("all");
  const [filterChannel, setFilterChannel] = useState<string>("all");
  const [filterDateFrom, setFilterDateFrom] = useState<Date | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<Date | undefined>(undefined);

  // Users for filter
  const [users, setUsers] = useState<UserOption[]>([]);
  // Profile name cache
  const [profileNames, setProfileNames] = useState<Record<string, string>>({});

  // Fetch distinct users from message_actions with __llm_request__
  useEffect(() => {
    if (!profile?.client_id) return;

    const fetchUsers = async () => {
      const { data } = await supabase
        .from("message_actions")
        .select("profile_id")
        .eq("client_id", profile.client_id)
        .eq("tool_name", "__llm_request__")
        .not("profile_id", "is", null);

      if (!data) return;

      const uniqueIds = Array.from(new Set(data.map((r) => r.profile_id).filter(Boolean))) as string[];
      if (uniqueIds.length === 0) return;

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", uniqueIds);

      if (!profiles) return;

      const nameMap: Record<string, string> = {};
      const opts: UserOption[] = profiles.map((p) => {
        const label = p.full_name || p.email || p.id.slice(0, 8);
        nameMap[p.id] = label;
        return { id: p.id, label };
      });
      setProfileNames(nameMap);
      setUsers(opts);
    };

    fetchUsers();
  }, [profile?.client_id]);

  const buildQuery = (offset: number) => {
    let query = supabase
      .from("message_actions")
      .select("id, conversation_id, message_id, profile_id, tool_input, tool_output, connector, created_at")
      .eq("client_id", profile!.client_id)
      .eq("tool_name", "__llm_request__")
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE);

    if (filterUser !== "all") {
      query = query.eq("profile_id", filterUser);
    }
    if (filterChannel !== "all") {
      query = query.eq("connector", filterChannel);
    }
    if (filterDateFrom) {
      const fromISO = new Date(filterDateFrom);
      fromISO.setHours(0, 0, 0, 0);
      query = query.gte("created_at", fromISO.toISOString());
    }
    if (filterDateTo) {
      const toISO = new Date(filterDateTo);
      toISO.setHours(23, 59, 59, 999);
      query = query.lte("created_at", toISO.toISOString());
    }

    return query;
  };

  useEffect(() => {
    if (!profile?.client_id) {
      setLoading(false);
      return;
    }

    const fetchEntries = async () => {
      setLoading(true);
      const { data, error } = await buildQuery(0);

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        setEntries([]);
        setHasMore(false);
      } else {
        const rows = (data ?? []) as LlmEntry[];
        setHasMore(rows.length > PAGE_SIZE);
        setEntries(rows.slice(0, PAGE_SIZE));
      }
      setLoading(false);
    };

    fetchEntries();
  }, [profile?.client_id, filterUser, filterChannel, filterDateFrom, filterDateTo]);

  // Fetch MCP tool calls when an entry is expanded
  const fetchToolCalls = useCallback(async (entry: LlmEntry) => {
    setToolCallsLoading(true);
    setToolCalls([]);

    let query = supabase
      .from("message_actions")
      .select("id, tool_name, tool_input, tool_output, connector, created_at")
      .eq("conversation_id", entry.conversation_id)
      .neq("tool_name", "__llm_request__")
      .order("created_at", { ascending: true });

    // Scope to the same message if available
    if (entry.message_id) {
      query = query.eq("message_id", entry.message_id);
    }

    const { data } = await query;
    setToolCalls((data as ToolCallAction[]) ?? []);
    setToolCallsLoading(false);
  }, []);

  const handleLoadMore = async () => {
    if (!profile?.client_id) return;
    setLoadingMore(true);

    const { data, error } = await buildQuery(entries.length);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      const rows = (data ?? []) as LlmEntry[];
      setHasMore(rows.length > PAGE_SIZE);
      setEntries((prev) => [...prev, ...rows.slice(0, PAGE_SIZE)]);
    }
    setLoadingMore(false);
  };

  const handleExpand = (entry: LlmEntry) => {
    const isExpanded = expandedId === entry.id;
    setExpandedId(isExpanded ? null : entry.id);
    setExpandedMsgIdx(null);
    setExpandedToolIdx(null);
    if (!isExpanded) {
      fetchToolCalls(entry);
    }
  };

  const getMessages = (entry: LlmEntry): { role: string; content: string }[] => {
    return entry.tool_input?.messages ?? [];
  };

  const getModel = (entry: LlmEntry): string => {
    return entry.tool_input?.model ?? "unknown";
  };

  const getToolsCount = (entry: LlmEntry): number | null => {
    return entry.tool_input?.tools_count ?? null;
  };

  const getContentPreview = (content: any): string => {
    const text = typeof content === "string" ? content : JSON.stringify(content);
    return text.length > 150 ? text.slice(0, 150) + "..." : text;
  };

  const getTotalChars = (entry: LlmEntry): number => {
    const messages = getMessages(entry);
    return messages.reduce((sum, m) => {
      const text = typeof m.content === "string" ? m.content : JSON.stringify(m.content);
      return sum + text.length;
    }, 0);
  };

  const getToolOutputSize = (output: string | null): number => {
    return output ? output.length : 0;
  };

  if (!profile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <ShieldAlert className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      <div>
        <h1 className="text-2xl font-semibold">LLM Data Exposure</h1>
        <p className="text-sm text-muted-foreground">
          Monitor what data is being sent to the LLM and what data MCP tools return. Review prompts, tool inputs/outputs, and conversation context for data governance.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">User:</span>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Channel:</span>
          <Select value={filterChannel} onValueChange={setFilterChannel}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              <SelectItem value="web">Web</SelectItem>
              <SelectItem value="telegram">Telegram</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">From:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[150px] justify-start text-left font-normal",
                  !filterDateFrom && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filterDateFrom ? format(filterDateFrom, "MMM d, yyyy") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterDateFrom}
                onSelect={setFilterDateFrom}
                disabled={(date) =>
                  date > new Date() || (filterDateTo ? date > filterDateTo : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">To:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[150px] justify-start text-left font-normal",
                  !filterDateTo && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filterDateTo ? format(filterDateTo, "MMM d, yyyy") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterDateTo}
                onSelect={setFilterDateTo}
                disabled={(date) =>
                  date > new Date() || (filterDateFrom ? date < filterDateFrom : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {(filterDateFrom || filterDateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterDateFrom(undefined);
              setFilterDateTo(undefined);
            }}
          >
            Clear dates
          </Button>
        )}
      </div>

      {/* Entries */}
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <BrainCircuit className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-1">No LLM requests found</p>
          <p className="text-sm text-muted-foreground">
            LLM request data will appear here as users interact with the AI assistant.
          </p>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {entries.map((entry) => {
              const messages = getMessages(entry);
              const model = getModel(entry);
              const toolsCount = getToolsCount(entry);
              const totalChars = getTotalChars(entry);
              const isExpanded = expandedId === entry.id;

              return (
                <Card
                  key={entry.id}
                  className={cn(
                    "transition-colors",
                    isExpanded ? "border-violet-300 dark:border-violet-700" : ""
                  )}
                >
                  {/* Header row */}
                  <button
                    className="flex w-full items-center gap-3 p-4 text-left"
                    onClick={() => handleExpand(entry)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}

                    <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(entry.created_at), "MMM d, yyyy HH:mm:ss")}
                      </span>

                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200">
                        {model}
                      </Badge>

                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {messages.length} msg{messages.length !== 1 ? "s" : ""}
                      </Badge>

                      {toolsCount != null && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {toolsCount} tool{toolsCount !== 1 ? "s" : ""}
                        </Badge>
                      )}

                      <Badge
                        variant="secondary"
                        className={cn(
                          "text-[10px] px-1.5 py-0",
                          totalChars > 5000
                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            : totalChars > 2000
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                              : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        )}
                      >
                        {totalChars.toLocaleString()} chars
                      </Badge>

                      {entry.connector && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {entry.connector}
                        </Badge>
                      )}

                      {entry.profile_id && profileNames[entry.profile_id] && (
                        <span className="text-xs text-muted-foreground ml-auto truncate max-w-[150px]">
                          {profileNames[entry.profile_id]}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t px-4 pb-4 pt-3 space-y-4">
                      {/* Section: Data sent to LLM */}
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Data sent to LLM
                          </span>
                        </div>

                        <ScrollArea className="max-h-[400px]">
                          <div className="space-y-2">
                            {messages.map((m, i) => (
                              <div
                                key={i}
                                className="rounded-lg border border-border bg-background"
                              >
                                <button
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left"
                                  onClick={() =>
                                    setExpandedMsgIdx(expandedMsgIdx === i ? null : i)
                                  }
                                >
                                  <Badge
                                    variant="secondary"
                                    className={cn(
                                      "text-[10px] px-1.5 py-0 shrink-0",
                                      m.role === "user"
                                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                                        : m.role === "system"
                                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200"
                                          : "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200"
                                    )}
                                  >
                                    {m.role}
                                  </Badge>
                                  <span className="truncate text-xs text-muted-foreground flex-1">
                                    {getContentPreview(m.content)}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground/60 shrink-0">
                                    {(typeof m.content === "string" ? m.content : JSON.stringify(m.content)).length.toLocaleString()} chars
                                  </span>
                                </button>
                                {expandedMsgIdx === i && (
                                  <div className="border-t">
                                    <pre className="px-3 py-2 whitespace-pre-wrap break-all text-xs max-h-64 overflow-auto bg-muted/30">
                                      {typeof m.content === "string"
                                        ? m.content
                                        : JSON.stringify(m.content, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>

                      {/* Section: MCP Tool Data Accessed */}
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <Database className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            MCP Tool Data Accessed
                          </span>
                          {!toolCallsLoading && toolCalls.length > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {toolCalls.length} call{toolCalls.length !== 1 ? "s" : ""}
                            </Badge>
                          )}
                        </div>

                        {toolCallsLoading ? (
                          <div className="flex items-center gap-2 py-4 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Loading tool calls...</span>
                          </div>
                        ) : toolCalls.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-border px-3 py-4 text-center">
                            <span className="text-xs text-muted-foreground">
                              No MCP tool calls recorded for this interaction.
                            </span>
                          </div>
                        ) : (
                          <ScrollArea className="max-h-[400px]">
                            <div className="space-y-2">
                              {toolCalls.map((tc, i) => {
                                const outputSize = getToolOutputSize(tc.tool_output);
                                const inputStr = tc.tool_input ? JSON.stringify(tc.tool_input) : "";
                                return (
                                  <div
                                    key={tc.id}
                                    className="rounded-lg border border-border bg-background"
                                  >
                                    <button
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left"
                                      onClick={() =>
                                        setExpandedToolIdx(expandedToolIdx === i ? null : i)
                                      }
                                    >
                                      <Wrench className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      <span className="font-mono text-xs font-medium text-foreground shrink-0">
                                        {tc.tool_name}
                                      </span>
                                      {tc.connector && (
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                                          {tc.connector}
                                        </Badge>
                                      )}
                                      <span className="truncate text-xs text-muted-foreground flex-1">
                                        {inputStr.length > 80 ? inputStr.slice(0, 80) + "..." : inputStr}
                                      </span>
                                      <Badge
                                        variant="secondary"
                                        className={cn(
                                          "text-[10px] px-1.5 py-0 shrink-0",
                                          outputSize > 5000
                                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                            : outputSize > 1000
                                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                              : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                        )}
                                      >
                                        {outputSize > 0 ? `${outputSize.toLocaleString()} chars` : "no output"}
                                      </Badge>
                                    </button>
                                    {expandedToolIdx === i && (
                                      <div className="border-t space-y-0">
                                        {/* Tool Input */}
                                        <div className="px-3 py-2 border-b bg-muted/20">
                                          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                            Input (sent to MCP)
                                          </span>
                                          <pre className="mt-1 whitespace-pre-wrap break-all text-xs max-h-48 overflow-auto">
                                            {tc.tool_input
                                              ? JSON.stringify(tc.tool_input, null, 2)
                                              : "—"}
                                          </pre>
                                        </div>
                                        {/* Tool Output */}
                                        <div className="px-3 py-2 bg-muted/30">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                              Output (data returned & exposed to LLM)
                                            </span>
                                            {outputSize > 5000 && (
                                              <Badge variant="destructive" className="text-[9px] px-1 py-0">
                                                Large payload
                                              </Badge>
                                            )}
                                          </div>
                                          <pre className="mt-1 whitespace-pre-wrap break-all text-xs max-h-64 overflow-auto">
                                            {tc.tool_output || "—"}
                                          </pre>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        )}
                      </div>

                      {/* LLM Response summary */}
                      {entry.tool_output && (
                        <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                            LLM Response Preview
                          </span>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-3">
                            {entry.tool_output.slice(0, 300)}
                            {entry.tool_output.length > 300 ? "..." : ""}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pb-4">
            <span className="text-sm text-muted-foreground">
              Showing {entries.length} {entries.length === 1 ? "request" : "requests"}
            </span>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Load more
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LlmObservability;
